import { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useDispatch } from "react-redux";
import Button from "../../../../common/Button";
import CustomInput from "../../../../common/CustomInput";
import CommonSlider from "../../../../common/CommonSlider";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import CommonSelect from "../../../clinical-services/services/select/common-creatable-select";
import ClinicalAlertTypeSelect from "./ClinicalAlertTypeSelect";
import { ALERT_TYPE_OPTIONS } from "./ClinicalAlertTypeSelect";
import { createClinicalAlerts, updateClinicalAlert } from "../../../../../redux/modules/patient/actions";
import { formatDateToISO, handleDateOptionChange as handleDateOptionChangeUtil } from "../../../../../helpers/functions";

const viewStatusOptions = [
  { value: "always-visible", label: "Always Visible" },
  { value: "pharmacist-view", label: "Pharmacist-Only View" },
];

const DEFAULT_VIEW_STATUS = { value: "always-visible", label: "Always Visible" };
const DEFAULT_INITIATED_BY = { value: "pharmacist", label: "Pharmacist" };

const initiatedByOptions = [
  { value: "pharmacist", label: "Pharmacist" },
  { value: "patient", label: "Patient" },
  { value: "caregiver", label: "Alternate Caregiver" },
  { value: "primary-provider", label: "Primary Provider" },
  { value: "nurse", label: "Nurse Practitioner" },
  { value: "other", label: "Other" },
];

// Helper: Normalize view status field
const normalizeViewStatus = (value) => {
  if (!value) return null;
  return value.toString().toLowerCase().includes('pharmacist') ? 'pharmacist-view' : 'always-visible';
};

// Helper: Normalize initiated by field
const normalizeInitiatedBy = (value) => {
  if (!value) return 'other';
  const val = value.toString().toLowerCase();
  if (val.includes('primary')) return 'primary-provider';
  if (val.includes('pharmacist')) return 'pharmacist';
  if (val.includes('patient')) return 'patient';
  if (val.includes('caregiver')) return 'caregiver';
  if (val.includes('nurse')) return 'nurse';
  return 'other';
};

// Predefined alert type options and labels (matching ClinicalAlertTypeSelect)
const PREDEFINED_ALERT_OPTIONS = ALERT_TYPE_OPTIONS.map((o) => (o && o.value ? o.value.toString() : ""));
const PREDEFINED_ALERT_LABELS = ALERT_TYPE_OPTIONS.map((o) => (o && o.label ? o.label.toString() : ""));

// Helper: Check if alert type text is "Other"
const isOtherAlertType = (alertTypeText) => {
  return alertTypeText?.toLowerCase() === "other";
};

// Helper: Check if alert type is in predefined list
const isPredefinedAlertType = (alertTypeText) => {
  if (!alertTypeText) return false;
  const normalizedText = alertTypeText.toLowerCase();
  return PREDEFINED_ALERT_LABELS.some(label => 
    label.toLowerCase() === normalizedText
  ) || PREDEFINED_ALERT_OPTIONS.some(opt => 
    opt.toLowerCase() === normalizedText
  );
};

const normalizeAlertText = (value) =>
  String(value || "").trim().toLowerCase();

const normalizeAlertForEdit = (alert) => ({
  ...alert,
  id: alert?.id,
  alertType: alert?.alertType || alert?.clinicalAlertType || alert?.ClinicalAlertType,
  viewStatus: alert?.viewStatus || alert?.viewType || alert?.ViewType,
  initiatedBy: alert?.initiatedBy || alert?.InitiatedBy,
  comments: alert?.comments || alert?.Comments,
  date: alert?.date || alert?.clinicalAlertDate || alert?.ClinicalAlertDate || alert?.createdAt,
  diagnosisDate: alert?.diagnosisDate !== undefined ? alert?.diagnosisDate : (alert?.DiagnosisDate !== undefined ? alert?.DiagnosisDate : null),
  clinicalAlertDateOption: alert?.clinicalAlertDateOption !== undefined ? alert?.clinicalAlertDateOption : (alert?.ClinicalAlertDateOption || null),
});

// Helper: Process alert type for form state
const processAlertType = (alertTypeText) => {
  if (!alertTypeText) {
    return { alertType: null, otherAlertText: "" };
  }

  const isOther = isOtherAlertType(alertTypeText);
  const isPredefined = isPredefinedAlertType(alertTypeText);

  if (isOther || !isPredefined) {
    // It's "Other" or a custom alert type
    return {
      alertType: { value: "other", label: "Other" },
      otherAlertText: isOther ? "" : alertTypeText
    };
  }

  // It's a predefined option
  return {
    alertType: { value: alertTypeText, label: alertTypeText },
    otherAlertText: ""
  };
};

export default function ClinicalAlertCreate({
  open,
  onClose,
  selectedAlert = null,
  patientId,
  create = true,
  onSave = () => {},
  clinicalAlerts = [],
  setClinicalAlerts = () => {},
  onOpenExistingAlertEdit = () => {},
}) {
  const dispatch = useDispatch();

  const [alertType, setAlertType] = useState(null);
  const [viewStatus, setViewStatus] = useState(DEFAULT_VIEW_STATUS);
  const [initiatedBy, setInitiatedBy] = useState(DEFAULT_INITIATED_BY);
  const [diagnosisDate, setDiagnosisDate] = useState("");
  const [clinicalAlertDateOption, setClinicalAlertDateOption] = useState(null);
  const [comments, setComments] = useState("");
  const [otherAlertText, setOtherAlertText] = useState("");

  // Error states
  const [alertTypeError, setAlertTypeError] = useState(false);
  const [viewStatusError, setViewStatusError] = useState(false);
  const [initiatedByError, setInitiatedByError] = useState(false);
  const [otherAlertError, setOtherAlertError] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [openExistingAlertsWarning, setOpenExistingAlertsWarning] = useState(false);
  const [openDuplicateAlertWarning, setOpenDuplicateAlertWarning] = useState(false);
  const [duplicateAlertRecord, setDuplicateAlertRecord] = useState(null);
  const [pendingAddAnother, setPendingAddAnother] = useState(false);

  // Handle date option change
  const handleDateOptionChange = (dateVal, dateOptionVal) => {
    handleDateOptionChangeUtil(dateVal, dateOptionVal, setDiagnosisDate, setClinicalAlertDateOption);
  };

  // Prefill form when editing - run when selectedAlert or create changes
  useEffect(() => {
    if (create || !selectedAlert) {
      // Reset form for create mode
      setAlertType(null);
      setViewStatus(DEFAULT_VIEW_STATUS);
      setInitiatedBy(DEFAULT_INITIATED_BY);
      setDiagnosisDate("");
      setClinicalAlertDateOption(null);
      setComments("");
      setOtherAlertText("");
      return;
    }

    // Get alert type (handle different possible field names)
    const alertTypeText = selectedAlert.alertType || selectedAlert.clinicalAlertType || selectedAlert.ClinicalAlertType;
    const { alertType, otherAlertText } = processAlertType(alertTypeText);
    setAlertType(alertType);
    setOtherAlertText(otherAlertText);

    // Get and normalize view status (handle different field names)
    const viewValue = selectedAlert.viewStatus || selectedAlert.viewType || selectedAlert.ViewType;
    const normalizedView = normalizeViewStatus(viewValue);
    setViewStatus(normalizedView ? {
      value: normalizedView,
      label: viewStatusOptions.find(i => i.value === normalizedView)?.label,
    } : null);

    // Get and normalize initiated by (handle different field names)
    const initiatedValue = selectedAlert.initiatedBy || selectedAlert.InitiatedBy;
    const normalizedInitiated = normalizeInitiatedBy(initiatedValue);
    setInitiatedBy({
      value: normalizedInitiated,
      label: initiatedByOptions.find(i => i.value === normalizedInitiated)?.label,
    });

    // Get diagnosis date (handle different field names)
    const dateValue = selectedAlert.diagnosisDate || selectedAlert.DiagnosisDate || selectedAlert.date || selectedAlert.clinicalAlertDate || selectedAlert.ClinicalAlertDate;
    setDiagnosisDate(dateValue ? (typeof dateValue === 'string' ? dateValue.split('T')[0] : new Date(dateValue).toISOString().split('T')[0]) : "");
    // Get date option
    setClinicalAlertDateOption(selectedAlert.clinicalAlertDateOption || selectedAlert.ClinicalAlertDateOption || null);

    // Get comments (handle different field names)
    setComments(selectedAlert.comments || selectedAlert.Comments || "");
  }, [selectedAlert, create, open]);

  // Clear otherAlertText when alertType changes away from "Other"
  useEffect(() => {
    const isOtherSelected = alertType?.value === "other" || alertType?.label?.toLowerCase() === "other";
    if (!isOtherSelected) {
      setOtherAlertText("");
      setOtherAlertError(false);
    }
  }, [alertType]);

  const isNoneSelected = alertType?.value === "none" || alertType?.label?.toLowerCase() === "none";
  const hasExistingClinicalAlerts = (clinicalAlerts || []).some((existingAlert) => {
    const existingId = existingAlert?.id;
    const selectedId = selectedAlert?.id;
    if (!create && selectedId && existingId === selectedId) return false;
    if (existingAlert?.status === "inactive") return false;
    const existingType =
      existingAlert?.alertType ||
      existingAlert?.clinicalAlertType ||
      existingAlert?.ClinicalAlertType ||
      "";
    return String(existingType).trim().toLowerCase() !== "none";
  });

  const handleAlertTypeChange = (nextAlertType) => {
    const isNextNone =
      nextAlertType?.value === "none" ||
      nextAlertType?.label?.toLowerCase() === "none";
    if (isNextNone && hasExistingClinicalAlerts) {
      setOpenExistingAlertsWarning(true);
      return;
    }
    setAlertType(nextAlertType);
  };

  // When 'None' is selected, clear and disable related fields
  useEffect(() => {
    if (isNoneSelected) {
      setViewStatus(null);
      setInitiatedBy(null);
    } else {
      if (!viewStatus) setViewStatus(DEFAULT_VIEW_STATUS);
      if (!initiatedBy) setInitiatedBy(DEFAULT_INITIATED_BY);
    }
  }, [isNoneSelected]);

  const resetForm = () => {
    setAlertType(null);
    setViewStatus(DEFAULT_VIEW_STATUS);
    setInitiatedBy(DEFAULT_INITIATED_BY);
    setDiagnosisDate("");
    setComments("");
    setOtherAlertText("");
    setClicked(false);
    setAlertTypeError(false);
    setViewStatusError(false);
    setInitiatedByError(false);
    setOtherAlertError(false);
    setOpenDuplicateAlertWarning(false);
    setDuplicateAlertRecord(null);
    setPendingAddAnother(false);
  };

  const handleSave = async (addAnother = false, duplicateAction = "prompt") => {
    setClicked(true);

    // Check if "Other" is selected
    const isOtherSelected = alertType?.value === "other" || alertType?.label?.toLowerCase() === "other";
    
    // Validate all fields (skip viewStatus/initiatedBy when "None" selected)
    const isAlertTypeValid = alertType && alertType.label && alertType.label.trim() !== "";
    const isOtherTextValid = isOtherSelected ? otherAlertText && otherAlertText.trim() !== "" : true;
    const isViewStatusValid = isNoneSelected ? true : !!viewStatus;
    const isInitiatedByValid = isNoneSelected ? true : !!initiatedBy;

    setAlertTypeError(!isAlertTypeValid);
    setOtherAlertError(isOtherSelected && !isOtherTextValid);
    setViewStatusError(!isViewStatusValid);
    setInitiatedByError(!isInitiatedByValid);

    if (!isAlertTypeValid || !isOtherTextValid || !isViewStatusValid || !isInitiatedByValid) return;

    // Use the custom text if "Other" is selected, otherwise use the alert type label
    const finalAlertType = isOtherSelected ? otherAlertText.trim() : alertType.label.trim();

    const shouldCheckDuplicate = create && !isOtherSelected && alertType?.value !== "none" &&
      isPredefinedAlertType(alertType?.label || alertType?.value);

    if (shouldCheckDuplicate && duplicateAction === "prompt") {
      const matchingAlert = (clinicalAlerts || []).find((existingAlert) => {
        if (existingAlert?.status === "inactive") return false;
        const existingType = existingAlert?.alertType || existingAlert?.clinicalAlertType || existingAlert?.ClinicalAlertType || "";
        return normalizeAlertText(existingType) === normalizeAlertText(finalAlertType);
      });

      if (matchingAlert) {
        setDuplicateAlertRecord(matchingAlert);
        setPendingAddAnother(addAnother);
        setOpenDuplicateAlertWarning(true);
        return;
      }
    }

    const clinicalAlertData = {
      id: create ? null : selectedAlert.id,
      alertType: finalAlertType,
      viewStatus: isNoneSelected ? null : viewStatus?.value,
      initiatedBy: isNoneSelected ? (initiatedBy?.value || 'other') : initiatedBy.value,
      diagnosisDate: diagnosisDate && diagnosisDate.trim() !== "" ? diagnosisDate.trim() : null,
      clinicalAlertDateOption: clinicalAlertDateOption,
      comments: comments.trim(),
      patientId,
      status: "active",
      createdAt: create ? new Date() : selectedAlert?.createdAt || new Date(),
      date: create ? new Date().toISOString().split('T')[0] : (selectedAlert?.date || selectedAlert?.clinicalAlertDate || selectedAlert?.ClinicalAlertDate || new Date().toISOString().split('T')[0]),
    };

    // Save to backend
    if (create && duplicateAction === "update" && duplicateAlertRecord?.id) {
      const { patientId: _, ...alertToUpdate } = {
        ...clinicalAlertData,
        id: duplicateAlertRecord.id,
        createdAt: duplicateAlertRecord.createdAt || clinicalAlertData.createdAt,
        date: duplicateAlertRecord.date || duplicateAlertRecord.clinicalAlertDate || duplicateAlertRecord.ClinicalAlertDate || clinicalAlertData.date,
      };
      await dispatch(updateClinicalAlert(duplicateAlertRecord.id, alertToUpdate));
    } else if (create) {
      await dispatch(createClinicalAlerts(patientId, [clinicalAlertData]));
    } else {
      const { patientId: _, ...alertToUpdate } = clinicalAlertData;
      await dispatch(updateClinicalAlert(selectedAlert.id, alertToUpdate));
    }

    setOpenDuplicateAlertWarning(false);
    setDuplicateAlertRecord(null);
    setPendingAddAnother(false);
    resetForm();
    await onSave(); // Wait for fetch to complete before closing
    if (!addAnother) onClose();
  };

  return (
    <CommonSlider open={open} onClose={onClose}>
      <div className="flex flex-col flex-1 h-full overflow-y-auto">
        <div className="relative flex-1">
          {/* Header */}
          <div className="px-8 py-6 bg-gray-20">
            <div className="flex items-start justify-between space-x-3">
              <div className="flex space-x-1">
                <h2 className="font-bold text-gray-900 text-display-xs">
                  {create ? "Add Clinical Alert" : "Edit Clinical Alert"}
                </h2>
              </div>
              <div className="flex items-center h-7">
                <Button
                  className="!p-1 !rounded-full !max-w-8 !min-w-8 !h-8 bg-blue-200 hover:!bg-REELTeal hover:!text-white !text-teal-300 !border-0"
                  iconComponent={<XMarkIcon className="w-5 h-5" aria-hidden="true" />}
                  onClick={() => onClose()}
                  type="primary-outline"
                  text=""
                  toolTipId="close-clinical-alert"
                />
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div className="px-8 py-6">
            <div className="grid grid-cols-12 gap-6">
            <ClinicalAlertTypeSelect
              label="Clinical Alert"
              value={alertType}
              onChange={handleAlertTypeChange}
              error={alertTypeError}
              isRequired={true}
              className="col-span-12"
            />

            {/* Show "Other" text input when "Other" is selected */}
            {(alertType?.value === "other" || alertType?.label?.toLowerCase() === "other") && (
              <div className="col-span-12">
                <CustomInput
                  name="otherAlertText"
                  className="col-span-12"
                  label="Other Clinical Alert"
                  placeholder="Enter custom clinical alert"
                  value={otherAlertText}
                  onChange={(e) => setOtherAlertText(e.target.value)}
                  type="text"
                  error={otherAlertError}
                  required={true}
                />
                {otherAlertError && (
                  <span className="text-red-200 text-xs">Other Alert is required</span>
                )}
              </div>
            )}

              <CommonSelect
                onChange={(value) => setViewStatus(value)}
                options={viewStatusOptions}
                label="View Status"
                isRequired={!isNoneSelected}
                disabled={isNoneSelected}
                value={viewStatus}
                className="col-span-12"
                error={viewStatusError}
                placeholder="Select View Status"
              />

              <CommonSelect
                onChange={(value) => setInitiatedBy(value)}
                options={initiatedByOptions}
                label="Initiated By"
                isRequired={!isNoneSelected}
                disabled={isNoneSelected}
                value={initiatedBy}
                className="col-span-12"
                error={initiatedByError}
                placeholder="Select Initiated By"
              />

              <CustomInput
                name="diagnosisDate"
                type="date"
                className="col-span-12"
                label="Diagnosis Date"
                placeholder="Select Diagnosis Date"
                value={diagnosisDate}
                dateOptionValue={clinicalAlertDateOption}
                showDateOptions={true}
                onDateOptionChange={handleDateOptionChange}
                showOptionalLabel={true}
              />

              <CustomInput
                name="comments"
                className="col-span-12"
                label="Comments / Description"
                placeholder="Comments / Description"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                type="textarea"
                inputClassName="min-h-[48px] max-h-[72px] overflow-y-auto resize-none whitespace-pre-wrap break-words leading-6"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-center py-6">
          <div className="flex items-center justify-center space-x-5">
            <Button
              className="min-w-[200px]"
              text="Cancel"
              type="secondary"
              onClick={() => {
                resetForm();
                onClose();
              }}
            />
            {create && (
              <Button
                className="min-w-[200px]"
                text="Save & Add Another"
                type="primary"
                onClick={() => handleSave(true)}
              />
            )}
            <Button
              className="min-w-[200px]"
              text={create ? "Save" : "Update & Save"}
              type="primary"
              onClick={() => handleSave(false)}
            />
          </div>
        </div>
      </div>
      <ConfirmationModalNew
        open={openExistingAlertsWarning}
        title="Existing Clinical Alerts"
        type={"primary"}
        confirmText={"Okay"}
        cancelText={null}
        verticalButtons={true}
        content={() => (
          <div>
            <span className="text-lg font-semibold">Clinical Alerts already exist on this chart.</span> 
            <br />
            <br />
            To select "None," please delete all existing clinical alerts.
          </div>
        )}
        onCross={() => setOpenExistingAlertsWarning(false)}
        handleClose={() => setOpenExistingAlertsWarning(false)}
        confirmAction={() => setOpenExistingAlertsWarning(false)}
      />
      <ConfirmationModalNew
        open={openDuplicateAlertWarning}
        title="Clinical Alert Already Exists"
        type={"primary"}
        confirmText={"Update Existing Alert"}
        cancelText={null}
        verticalButtons={true}
        buttonClassName="w-full !min-h-[52px] !rounded-[6px]"
        content={() => (
          <div className="text-lg">
            This clinical alert already exists in the Patient Chart.
          </div>
        )}
        handleClose={() => {
          setOpenDuplicateAlertWarning(false);
          setDuplicateAlertRecord(null);
          setPendingAddAnother(false);
        }}
        onCross={() => {
          setOpenDuplicateAlertWarning(false);
          setDuplicateAlertRecord(null);
          setPendingAddAnother(false);
        }}
        confirmAction={() => {
          if (duplicateAlertRecord?.id) {
            const normalizedAlert = normalizeAlertForEdit(duplicateAlertRecord);
            setOpenDuplicateAlertWarning(false);
            setDuplicateAlertRecord(null);
            setPendingAddAnother(false);
            onOpenExistingAlertEdit(normalizedAlert);
            return;
          }
          handleSave(pendingAddAnother, "update");
        }}
        extraButtonComponent={() => (
          <>
            <Button
              className="w-full !min-h-[52px] !rounded-[6px]"
              type="primary-outline"
              text="Create New Entry"
              onClick={() => {
                setOpenDuplicateAlertWarning(false);
                setDuplicateAlertRecord(null);
                setPendingAddAnother(false);
                handleSave(pendingAddAnother, "create");
              }}
            />
            <Button
              className="w-full !min-h-[52px] !rounded-[6px]"
              type="primary-outline"
              text="Cancel"
              onClick={() => {
                setOpenDuplicateAlertWarning(false);
                setDuplicateAlertRecord(null);
                setPendingAddAnother(false);
              }}
            />
          </>
        )}
      />
    </CommonSlider>
  );
}

